












































import { Component, Vue } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { EventEmitter } from 'events'
import WebSocketProvider from '@/websocket'
import { WebSocketMessageMode } from '@/websocket/constants'
import { WebSocketCallMessageTypes } from '@/websocket/receivers/call'
import { Worker, Patient } from '@/types/user'

const enum InvitationToolEventTypes {
  CLICK_WINDOW
}

@Component({
  components: {
    Loading: () => import('@/components/common/loadings/Index.vue')
  }
})
export default class VideoCallInvitationTool extends Vue {
  $store!: ExStore
  innerCheckedUsers: { [id: number]: boolean } = {}
  showMenu = false
  pending = false
  isLoaded = false

  events = new EventEmitter()
  clickWindowHandler = (event: Event) =>
    this.events.emit(InvitationToolEventTypes.CLICK_WINDOW, event)

  get onlineStatus() {
    return this.$store.getters['onlineStatus/onlineStatus']
  }

  get checkedUsers() {
    this.invitableUsers.forEach(({ id }) => {
      this.innerCheckedUsers[id] = this.innerCheckedUsers[id] === true
    })
    return this.innerCheckedUsers
  }

  get roomId() {
    return this.$store.getters['videoCallRoom/roomId']
  }

  get roomSecret() {
    return this.$store.getters['videoCallRoom/roomSecret']
  }

  get pinCode() {
    return this.$store.getters['videoCallRoom/pinCode']
  }

  get candidates() {
    const teams = this.$store.getters['team/patients']

    const patientParticipant = teams.find(
      p => this.participants[p.id] || this.pendingUsers[p.id]
    )

    const participantAndPendingUserIds = Object.keys(this.participants).concat(
      Object.keys(this.pendingUsers)
    )
    const scope = patientParticipant
      ? [patientParticipant]
      : teams.filter(patient => {
          const workers = patient.workers.reduce(
            (workers, worker) =>
              Object.assign(workers, { [worker.id]: worker }),
            {} as { [id: number]: Worker }
          )

          return (
            participantAndPendingUserIds.reduce(
              (count, id) => Number(workers[Number(id)] !== undefined) + count,
              0
            ) === participantAndPendingUserIds.length
          )
        })

    const candidateWorkers = scope.reduce((workers, patient) => {
      patient.workers.forEach(worker => {
        workers[worker.id] = worker
      })
      return workers
    }, {} as { [id: string]: Worker })

    return (scope as Array<Patient | Worker>).concat(
      Object.keys(candidateWorkers)
        .map(id => candidateWorkers[id])
        .sort((u1, u2) => (u1.occupation.code < u2.occupation.code ? -1 : 1))
    )
  }

  get participants() {
    return this.$store.getters['videoCallRoom/participants'].reduce(
      (ids, p) => ({ ...ids, [p.id]: true }),
      {} as { [id: number]: boolean }
    )
  }

  get pendingUsers() {
    return this.$store.getters['videoCallRoom/pendingUsers']
  }

  get invitableUsers() {
    return this.candidates.filter(({ id }) => !this.participants[id])
  }

  isOnline(id: number) {
    return this.onlineStatus[id] && this.onlineStatus[id].isOnline
  }

  isDisabled(id: number) {
    return this.pendingUsers[id] || !this.isOnline(id)
  }

  async handleOpenMenuButton() {
    this.showMenu = !this.showMenu

    if (!this.isLoaded) {
      this.pending = true
      await this.$store.dispatch('team/updatePatients')
      this.pending = false
    }

    this.isLoaded = true

    if (!this.showMenu) {
      return
    }

    const handler = (event: Event) => {
      if (!this.$refs.menu) {
        return
      }

      if (
        (this.$refs.menu as HTMLElement).contains(event.target as HTMLElement)
      ) {
        return
      }

      this.showMenu = false
      this.events.removeAllListeners(InvitationToolEventTypes.CLICK_WINDOW)
    }

    window.setTimeout(
      () => this.events.on(InvitationToolEventTypes.CLICK_WINDOW, handler),
      50
    )
  }

  handleCheck(id: number, event: Event) {
    const element = event.target as HTMLInputElement
    Vue.set(this.innerCheckedUsers, id, element.checked)
  }

  handleClickSubmit() {
    this.showMenu = false
    this.events.removeAllListeners(InvitationToolEventTypes.CLICK_WINDOW)

    const checkedUsers = this.invitableUsers.filter(
      ({ id }) => this.checkedUsers[id] === true
    )
    checkedUsers.forEach(user => {
      this.$store.commit('videoCallRoom/removeDeniedUser', { id: user.id })
      this.$store.commit('videoCallRoom/addPendingUser', user)
    })

    const to = checkedUsers.map(({ id }) => id)

    const webSocket = WebSocketProvider.createConnection()

    webSocket.send(WebSocketMessageMode.CALL, {
      type: WebSocketCallMessageTypes.REQUEST,
      secret: this.roomSecret,
      roomId: this.roomId,
      pin: this.pinCode,
      to
    })

    this.innerCheckedUsers = {}
  }

  created() {
    window.addEventListener('click', this.clickWindowHandler)
  }

  destroyed() {
    window.removeEventListener('click', this.clickWindowHandler)
  }
}
